import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { IApiResponse } from '@core/interfaces/api-response.interface';
import { EPlatformFeature } from '@enums/campaign.enum';
import {
  ICampaignAnalyticsApiResponse,
  ICreatorAnalyticsApi,
  ICreatorAnalyticsSliceApi,
  ICreatorCtaKpiStatusApi,
  IMatchedKpiAnalyticsApi,
  IReprocessCampaignAnalyticsApiResponse,
  TSocialContent,
} from '@interfaces/campaign-analytics-api.interface';
import { TFinding } from '@interfaces/deliverable-analytics.interface';
import {
  IAnnotatorTaskInput,
  IAnnotatorTaskResponse,
} from '@pages/annotator-tasks/video-analytics/video-analytics.interface';
import { ICreatorChannelAnalyticsResponse } from '@pages/creators/creators.interface';
import { camelCase } from 'lodash';
import { map, Observable, timeout } from 'rxjs';

const FINDING_ANALYTICS_IMAGE_TIMEOUT_MS = 15000;

@Injectable({
  providedIn: 'root',
})
export class CampaignAnalyticsService {
  private readonly http = inject(HttpClient);

  public getCampaignAnalytics(campaignId: string, params?: HttpParams): Observable<ICampaignAnalyticsApiResponse> {
    return this.http.get<ICampaignAnalyticsApiResponse>(`/campaign-analytics/${campaignId}`, { params });
  }

  public reprocessCampaignAnalyticsAdmin(campaignId: string): Observable<IReprocessCampaignAnalyticsApiResponse> {
    return this.http.delete<IReprocessCampaignAnalyticsApiResponse>(`/campaign-analytics/admin/${campaignId}`);
  }

  public getCampaignAnalyticsAdmin(campaignId: string, params?: HttpParams): Observable<ICampaignAnalyticsApiResponse> {
    return this.http.get<ICampaignAnalyticsApiResponse>(`/campaign-analytics/admin/${campaignId}`, { params });
  }

  public getCreatorBriefingCampaignAnalytics(
    briefingId: string,
    params?: HttpParams,
  ): Observable<ICampaignAnalyticsApiResponse> {
    return this.http.get<ICampaignAnalyticsApiResponse>(`/campaign-analytics/briefing/creator/${briefingId}`, {
      params,
    });
  }

  public getExportCampaignAnalytics(exportId: string, params?: HttpParams): Observable<ICampaignAnalyticsApiResponse> {
    return this.http.get<ICampaignAnalyticsApiResponse>(`/campaign-analytics/export/${exportId}`, { params });
  }

  public getBulkCampaignAnalytics(campaignIds: string[]): Observable<ICampaignAnalyticsApiResponse[]> {
    return this.http.get<ICampaignAnalyticsApiResponse[]>(`/campaign-analytics`, {
      params: new HttpParams().set('campaign_ids', campaignIds.join(',')),
    });
  }

  public getBulkCampaignAnalyticsAdmin(campaignIds: string[]): Observable<ICampaignAnalyticsApiResponse[]> {
    return this.http.get<ICampaignAnalyticsApiResponse[]>(`/campaign-analytics/admin`, {
      params: new HttpParams().set('campaign_ids', campaignIds.join(',')),
    });
  }

  public getBulkCampaignAnalyticsCreatorBriefing(campaignIds: string[]): Observable<ICampaignAnalyticsApiResponse[]> {
    return this.http.get<ICampaignAnalyticsApiResponse[]>(`/campaign-analytics/briefing`, {
      params: new HttpParams().set('campaign_ids', campaignIds.join(',')),
    });
  }

  public getCreatorAnalyticsSlices(params?: HttpParams): Observable<IApiResponse<ICreatorAnalyticsSliceApi[]>> {
    return this.http.get<IApiResponse<ICreatorAnalyticsSliceApi[]>>(`/campaign-analytics/creator-analytics-slices`, {
      params,
    });
  }

  /**
   * Raw `creator-analytics` shell list (planned CTAs + status, no slices - callers combine this
   * with getCreatorAnalyticsSlices separately, same two-call split campaign-details already uses
   * per campaign). Used by creator-details' Deliverables tab to fetch one shell per campaign for
   * a single creator in one request via `campaign_id__in`, instead of the single-campaign
   * `joins=creator_analytics` embed campaign-details gets from getCampaignAnalytics.
   */
  public getCreatorAnalyticsShells(params?: HttpParams): Observable<IApiResponse<ICreatorAnalyticsApi[]>> {
    return this.http.get<IApiResponse<ICreatorAnalyticsApi[]>>(`/campaign-analytics/creator-analytics`, {
      params,
    });
  }

  public getCreatorAnalyticsShellsAdmin(params?: HttpParams): Observable<IApiResponse<ICreatorAnalyticsApi[]>> {
    return this.http.get<IApiResponse<ICreatorAnalyticsApi[]>>(`/campaign-analytics/creator-analytics/admin`, {
      params,
    });
  }

  public getCreatorAnalyticsSlicesAdmin(params?: HttpParams): Observable<IApiResponse<ICreatorAnalyticsSliceApi[]>> {
    return this.http.get<IApiResponse<ICreatorAnalyticsSliceApi[]>>(
      `/campaign-analytics/creator-analytics-slices/admin`,
      { params },
    );
  }

  public getCreatorAnalyticsSlicesExport(
    exportId: string,
    params?: HttpParams,
  ): Observable<IApiResponse<ICreatorAnalyticsSliceApi[]>> {
    return this.http.get<IApiResponse<ICreatorAnalyticsSliceApi[]>>(
      `/campaign-analytics/creator-analytics-slices/export/${exportId}`,
      { params },
    );
  }

  public getCreatorAnalyticsSlicesCreatorBriefing(
    briefingId: string,
    params?: HttpParams,
  ): Observable<IApiResponse<ICreatorAnalyticsSliceApi[]>> {
    return this.http.get<IApiResponse<ICreatorAnalyticsSliceApi[]>>(
      `/campaign-analytics/creator-analytics-slices/briefing/creator/${briefingId}`,
      { params },
    );
  }

  public getCreatorAnalyticsSlicesCtaKpiStatus(params?: HttpParams): Observable<ICreatorCtaKpiStatusApi[]> {
    return this.http.get<ICreatorCtaKpiStatusApi[]>(`/campaign-analytics/creator-analytics-slices/cta-kpi-status`, {
      params,
    });
  }

  public getCreatorAnalyticsSlicesCtaKpiStatusExport(
    campaignExportId: string,
    params?: HttpParams,
  ): Observable<ICreatorCtaKpiStatusApi[]> {
    return this.http.get<ICreatorCtaKpiStatusApi[]>(
      `/campaign-analytics/creator-analytics-slices/cta-kpi-status/export/${campaignExportId}`,
      { params },
    );
  }

  public getCreatorAnalyticsSlicesCtaKpiStatusCreatorBriefing(
    briefingId: string,
    params?: HttpParams,
  ): Observable<ICreatorCtaKpiStatusApi[]> {
    return this.http.get<ICreatorCtaKpiStatusApi[]>(
      `/campaign-analytics/creator-analytics-slices/cta-kpi-status/briefing/creator/${briefingId}`,
      { params },
    );
  }

  public getCreatorAnalyticsSlicesMatchedKpis(params?: HttpParams): Observable<IMatchedKpiAnalyticsApi[]> {
    return this.http
      .get<IMatchedKpiAnalyticsApi[]>(`/campaign-analytics/creator-analytics-slices/matched-kpis`, {
        params,
      })
      .pipe(
        map((matchedKpis) =>
          matchedKpis.map((matchedKpi) => ({
            ...matchedKpi,
            platformFeature: camelCase(matchedKpi.platformFeature) as EPlatformFeature,
          })),
        ),
      );
  }

  public getCreatorAnalyticsSlicesMatchedKpisExport(
    campaignExportId: string,
    params?: HttpParams,
  ): Observable<IMatchedKpiAnalyticsApi[]> {
    return this.http
      .get<IMatchedKpiAnalyticsApi[]>(
        `/campaign-analytics/creator-analytics-slices/matched-kpis/export/${campaignExportId}`,
        {
          params,
        },
      )
      .pipe(
        map((matchedKpis) =>
          matchedKpis.map((matchedKpi) => ({
            ...matchedKpi,
            platformFeature: camelCase(matchedKpi.platformFeature) as EPlatformFeature,
          })),
        ),
      );
  }

  public getCreatorAnalytics(creatorId: string): Observable<ICreatorChannelAnalyticsResponse> {
    return this.http.get<ICreatorChannelAnalyticsResponse>(`/creator-analytics/${creatorId}`);
  }

  public getAnnotatorTask(analyticsId: string): Observable<IAnnotatorTaskResponse> {
    return this.http.get<IAnnotatorTaskResponse>(`/annotator-tasks/${analyticsId}`);
  }

  public getAnnotatorTasks(params?: HttpParams): Observable<IApiResponse<IAnnotatorTaskResponse[]>> {
    return this.http.get<IApiResponse<IAnnotatorTaskResponse[]>>(`/annotator-tasks`, { params });
  }

  public updateAnnotatorTask(
    campaignId: string,
    annotatorTask: IAnnotatorTaskInput,
  ): Observable<ICampaignAnalyticsApiResponse> {
    return this.http.post<ICampaignAnalyticsApiResponse>(`/campaign-analytics/${campaignId}/annotator`, annotatorTask);
  }

  public updateAnnotatorTasks(
    campaignId: string,
    annotatorTasks: IAnnotatorTaskInput[],
  ): Observable<ICampaignAnalyticsApiResponse> {
    return this.http.post<ICampaignAnalyticsApiResponse>(
      `/campaign-analytics/${campaignId}/annotators`,
      annotatorTasks,
    );
  }

  public getAnnotatorTaskInputs(
    campaignId: string,
    params?: HttpParams,
  ): Observable<IApiResponse<IAnnotatorTaskInput[]> | null> {
    return this.http.get<IApiResponse<IAnnotatorTaskInput[]>>(`/campaign-analytics/${campaignId}/annotator`, {
      params,
    });
  }

  public getFindings(params?: HttpParams): Observable<IApiResponse<TFinding[]>> {
    return this.http.get<IApiResponse<TFinding[]>>(`/findings`, { params });
  }

  public getFindingsExport(exportId: string, params?: HttpParams): Observable<IApiResponse<TFinding[]>> {
    return this.http.get<IApiResponse<TFinding[]>>(`/findings/export/${exportId}`, { params });
  }

  // With a campaignExportId it reads through the public export route (exempt from authorization);
  // there is no briefing variant - findings are never shown in creatorBriefingMode.
  public getFindingAnalyticsImage(findingId: string, campaignExportId?: string | null): Observable<Blob> {
    const url = campaignExportId
      ? `/findings/export/${campaignExportId}/${findingId}/analytics-image`
      : `/findings/${findingId}/analytics-image`;

    return this.http.get(url, { responseType: 'blob' }).pipe(timeout(FINDING_ANALYTICS_IMAGE_TIMEOUT_MS));
  }

  public getCreatorSocialContents(creatorId: string, params?: HttpParams): Observable<IApiResponse<TSocialContent[]>> {
    return this.http.get<IApiResponse<TSocialContent[]>>(`/social-contents/creators/${creatorId}`, { params });
  }

  public creatorAnalyticsCampaign(campaignId: string): Observable<ICreatorChannelAnalyticsResponse[]> {
    return this.http.get<ICreatorChannelAnalyticsResponse[]>(`/creator-analytics/campaigns/${campaignId}`);
  }

  public creatorAnalyticsCampaignExport(campaignExportId: string): Observable<ICreatorChannelAnalyticsResponse[]> {
    return this.http.get<ICreatorChannelAnalyticsResponse[]>(`/creator-analytics/campaigns/export/${campaignExportId}`);
  }
}
